import { z } from "zod";
/**
 * Fallback function returns default object in case of null or undefined value, otherwise it creates a new instance of the class using the provided value.
 */
export function ensureInstance<T>(
  value: unknown,
  Ctor: new (init?: Partial<T>) => T,
  fallback?: () => T
): T {
  if (value instanceof Ctor) {
    return value;
  }

  if (value == null) {
    return fallback ? fallback() : new Ctor();
  }

  return new Ctor(value as Partial<T>);
}



export function ensureInstanceArray<T>(
  value: unknown,
  Ctor: new (init?: Partial<T>) => T,
  itemFallback?: () => T
): T[] {

  if (!Array.isArray(value)) {
    return [];
  }

  return value.map(item =>
    ensureInstance(item, Ctor, itemFallback)
  ).filter(item => !(item === null || item === undefined));

}


/**
 * Simple guard to if two shapes align (have same keys and types) or not.
 */
export type TypesMatch<A, B> =
  (<T>() => T extends A ? 1 : 2) extends
  (<T>() => T extends B ? 1 : 2)
   ? (<T>() => T extends B ? 1 : 2) extends (<T>() => T extends A ? 1 : 2)
      ? true
      : false
    : false;

export type TypeAssert<T extends true> = T;




// This is a more detailed type assertion that provides a report of the differences between the expected and actual types when they do not match. It identifies missing keys, extra keys, and mismatched types, making it easier to understand why two types do not align.
type Simplify<T> = { [K in keyof T]: T[K] } & {};
type EmptyObject = Record<never, never>;

type MissingKeys<Expected, Actual> = Exclude<keyof Expected, keyof Actual>;
type ExtraKeys<Expected, Actual> = Exclude<keyof Actual, keyof Expected>;

type MismatchedKeys<Expected, Actual> = {
  [K in keyof Expected & keyof Actual]:
    [Expected[K]] extends [Actual[K]]
      ? ([Actual[K]] extends [Expected[K]] ? never : K)
      : K
}[keyof Expected & keyof Actual];

type MismatchReport<Expected, Actual> = Simplify<
  (MissingKeys<Expected, Actual> extends never ? EmptyObject : { missingKeys: MissingKeys<Expected, Actual> }) &
  (ExtraKeys<Expected, Actual> extends never ? EmptyObject : { extraKeys: ExtraKeys<Expected, Actual> }) &
  (MismatchedKeys<Expected, Actual> extends never ? EmptyObject : {
    mismatchedTypes: {
      [K in MismatchedKeys<Expected, Actual>]: { expected: Expected[K]; actual: Actual[K] }
    }
  })
>;


// If aligned => true, else => a readable report object
export type AssertAligned<Expected, Actual> =
  keyof MismatchReport<Expected, Actual> extends never
    ? true
    : MismatchReport<Expected, Actual>;

export type AssertSchemaMatchesClass<Schema, Class> = Prettify<AssertAligned<z.infer<Schema>, Pick<Class, keyof Class>>>;










export function isString(value: unknown): value is string {
    return typeof value === 'string' || value instanceof String;
}


/**
 * Beautify or simplify types
 * takes a generic parameter T and maps over its keys using a mapped type to essentially create a new type that has the same properties as T.
 * It is easier to read when getting IDE support. 
 * 
 * 
 *   type Prettify<T>: This is a declaration of a new type called Prettify that takes a generic parameter T. T is a placeholder for any type that you want to use with Prettify.
 *   [K in keyof T]: This is a mapped type. It iterates over all keys of T (represented by K). keyof T is a type that represents all keys of T.
 *   T[K]: This is an indexed access type. It represents the type of property K in T.
 *   & {}: This is an intersection type. It combines multiple types into one. In this case, it's intersecting the mapped type with an empty object type. However, since an intersection with an empty object doesn't change the type, this part doesn't have any effect on the Prettify type.
 *
 **/
export type Prettify<T> = {
  [K in keyof T]: T[K];
} & {};

/**
 * to allow all props to be optional, but at least one prop is required
 */  
export type OneOf<T> = {
    [K in keyof T]-?: Pick<T, K> & Partial<T>
}[keyof T]

